import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { groupBy, upperFirst } from 'lodash';
import styles from './RetailerPricingTable.module.scss';
import { useSelector } from 'react-redux';
import {
  getAvailablePriceTypes,
  getAvailableVariantIdentifiers,
  getVariantPrices,
  getVariantStatus,
} from '../../../utils/pricings';
import cn from 'classnames';
import { TableEmptyData } from '../../tables/TableEmptyData';
import { TableErrorBanner } from '../../tables/TableErrorBanner';
import { TableWrapper } from '../../tables/TableWrapper';
import { IMAGE_COLUMN, MODEL_COLUMN, SALE_PRICE_COLUMN, STATUS_COLUMN } from '../../../constants/pricings';
import { PricingTablePrices } from '../../tables/pricing/PricingTablePrices';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';

export function RetailerPricingTable({ refetch }) {
  const { retailer_id: retailerId } = useSelector((state) => state.retailer.current);
  const {
    isError,
    data,
    table: { hiddenColumns },
    filters,
  } = useSelector((state) => state.pricings);

  const [expanded, setExpanded] = useState({});
  const dataByModels = useMemo(() => (data ? groupBy(data, (datum) => datum.model) : {}), [data]);
  const hasExpandedRow = useMemo(() => Object.values(expanded).some((value) => value), [expanded]);

  useEffect(() => {
    setExpanded({});
  }, [data]);

  const calculateRowSpan = (variants) => {
    return (
      variants.length +
      variants.reduce((sumOfPriceColumns, variant) => {
        if (expanded[variant.identifiers.sku]) {
          sumOfPriceColumns += Object.keys(variant.prices).length;
        }
        return sumOfPriceColumns;
      }, 0)
    );
  };

  const availablePriceTypes = getAvailablePriceTypes(data).filter((priceType) => !hiddenColumns.includes(priceType));
  const hasSalePriceColumn = availablePriceTypes.includes(SALE_PRICE_COLUMN);
  const multipleCurrenciesColSpan = useMemo(
    () => availablePriceTypes.length + (hasSalePriceColumn ? 2 : 0) + 1,
    [availablePriceTypes, hasSalePriceColumn],
  );

  if (isError) return <TableErrorBanner onRetry={refetch} />;
  if (!data) return null;

  let isEvenRow = false;

  const availableIdentifiers = getAvailableVariantIdentifiers(data).filter(
    (identifier) => !hiddenColumns.includes(identifier),
  );

  const toggleExpand = (sku) => {
    setExpanded((prev) => ({
      ...prev,
      [sku]: !prev[sku],
    }));
  };

  const toggleExpandAll = () => {
    const newExpanded = data.reduce((allSkus, variant) => {
      const { hasMultipleCurrencies } = getVariantPrices(variant.prices);
      if (hasMultipleCurrencies) {
        allSkus[variant.identifiers.sku] = !hasExpandedRow;
      }
      return allSkus;
    }, {});
    setExpanded(newExpanded);
  };

  const renderPriceColumns = (prices, currency) => {
    return <PricingTablePrices priceTypes={availablePriceTypes} prices={prices[currency]} currency={currency} />;
  };

  const renderCurrenciesColumns = (prices, currency, showMultipleCurrenciesCell, sku) => {
    if (!showMultipleCurrenciesCell) {
      return renderPriceColumns(prices, currency);
    }

    return (
      <td
        colSpan={multipleCurrenciesColSpan}
        className={cn(styles.cellMultiple, expanded[sku] ? styles.cellMultipleExpanded : '')}
        onClick={() => toggleExpand(sku)}
      >
        Multiple Currencies
      </td>
    );
  };

  if (data.length === 0) {
    return <TableEmptyData />;
  }

  const renderRow = (variant, newTableRow = true) => {
    const { hasPrices, hasMultipleCurrencies, prices } = getVariantPrices(variant.prices);
    const status = getVariantStatus(variant.permissions, [retailerId]);
    const { sku } = variant.identifiers;
    const currency = hasPrices ? Object.keys(prices)[0] : undefined;
    const showMultipleCurrenciesCell = hasMultipleCurrencies && filters.currencies.length > 1;

    const cells = (
      <>
        {availableIdentifiers.map((identifier) => (
          <td key={identifier}>{variant.identifiers[identifier] || NO_VALUE_PLACEHOLDER}</td>
        ))}
        {hiddenColumns.includes(STATUS_COLUMN) ? null : <td>{status}</td>}
        {hasPrices ? (
          renderCurrenciesColumns(prices, currency, showMultipleCurrenciesCell, sku)
        ) : (
          <td colSpan={multipleCurrenciesColSpan} className={styles.cellNoPrices}>
            No available prices
          </td>
        )}
      </>
    );

    return (
      <Fragment key={sku}>
        {newTableRow ? (
          <tr key={sku} className={isEvenRow ? styles.rowEven : styles.rowOdd}>
            {cells}
          </tr>
        ) : (
          cells
        )}
        {newTableRow && renderMultipleCurrencyRows(variant)}
      </Fragment>
    );
  };

  const renderMultipleCurrencyRows = (variant) => {
    const { hasMultipleCurrencies, prices } = getVariantPrices(variant.prices);
    const { sku } = variant.identifiers;
    const showMultipleCurrenciesCell = hasMultipleCurrencies && filters.currencies.length > 1;

    if (!showMultipleCurrenciesCell) return null;

    return Object.keys(prices).map((currency) => (
      <tr
        key={`${sku}-${currency}`}
        className={cn(
          styles.rowCurrency,
          expanded[sku] ? styles.rowExpanded : '',
          isEvenRow ? styles.rowEven : styles.rowOdd,
        )}
      >
        <td
          colSpan={availableIdentifiers.length + (hiddenColumns.includes(STATUS_COLUMN) ? 0 : 1)}
          className={styles.rowCurrencyFirstCell}
        />
        {renderPriceColumns(prices, currency)}
      </tr>
    ));
  };

  return (
    <TableWrapper hasExpandedRow={hasExpandedRow} onExpandAll={toggleExpandAll} className={styles.pricingTable}>
      <thead>
        <tr>
          {hiddenColumns.includes(IMAGE_COLUMN) ? null : <th className={styles.headerImage}></th>}
          {hiddenColumns.includes(MODEL_COLUMN) ? null : <th className={styles.headerModel}>Model</th>}
          {availableIdentifiers.map((identifier) => (
            <th key={identifier} className={styles[`header-${identifier}`]}>
              {identifier.toUpperCase()}
            </th>
          ))}
          {hiddenColumns.includes(STATUS_COLUMN) ? null : <th className={styles.headerStatus}>Status</th>}
          <th className={styles.headerCurrency}>Currency</th>
          {availablePriceTypes.map((column) => (
            <th className={styles.headerPrice} key={column}>
              {upperFirst(column)} price
            </th>
          ))}
          {hasSalePriceColumn ? (
            <>
              <th className={styles.headerDate}>Sale Start</th>
              <th className={styles.headerDate}>Sale End</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {Object.entries(dataByModels).map(([model, modelVariants]) => {
          const [firstVariant, ...restOfVariants] = modelVariants;
          const rowSpan = calculateRowSpan(modelVariants);
          isEvenRow = !isEvenRow;

          return (
            <Fragment key={model}>
              <tr key={model} className={isEvenRow ? styles.rowEven : styles.rowOdd}>
                {hiddenColumns.includes(IMAGE_COLUMN) ? null : (
                  <td className={styles.cellImage} rowSpan={rowSpan}>
                    <div className={styles.cellImageWrapper}>
                      {firstVariant.images?.length ? (
                        <img src={firstVariant.images[0]} alt={firstVariant.translatedFields.en.title} />
                      ) : null}
                    </div>
                  </td>
                )}
                {hiddenColumns.includes(MODEL_COLUMN) ? null : (
                  <td className={styles.cellHeader} rowSpan={rowSpan}>
                    <div className={styles.cellHeaderContent}>
                      <label className={styles.cellModel}>{firstVariant.model}</label>
                      <span className={styles.cellTitle}>{firstVariant.translatedFields.en.title}</span>
                    </div>
                  </td>
                )}
                {renderRow(firstVariant, false)}
              </tr>
              {renderMultipleCurrencyRows(firstVariant)}
              {restOfVariants.map((variant) => renderRow(variant))}
            </Fragment>
          );
        })}
      </tbody>
    </TableWrapper>
  );
}

RetailerPricingTable.propTypes = {
  refetch: PropTypes.func.isRequired,
};
